import { useCallback, useMemo, useRef } from "react"

import { useScopedStorageKey } from "@/workspaces/scope"

import { PAGE_NAVIGATION_KEYS } from "@/lib/pageNavigationKeys"
import { useCollectionPageShortcuts } from "@/hooks/useCollectionPageShortcuts"
import {
  ESCAPE_SHORTCUT_PRIORITY,
  useEscapeShortcut,
} from "@/hooks/useEscapeShortcut"
import { usePageCommands } from "@/hooks/usePageCommands"
import { usePageShortcuts, type PageShortcut } from "@/hooks/usePageShortcuts"
import {
  usePersistedCollectionNavigation,
  usePersistedCollectionNavigationItems,
} from "@/hooks/usePersistedPageNavigation"
import type { RunsTableResult } from "@/types/tables/RunsTable"
import type { SimulationsTableResult } from "@/types/tables/SimulationsTable"

import { MULTI_RUN_LIMIT } from "../../multi-run"
import { RUNS_VIEW_MODE, type RunsViewMode } from "../types/viewMode"
import {
  getSelectedRunIds,
  getSelectedRunKeySet,
  type RunsTableSelection,
} from "../utils/runsTableState"

export function useRunsTableInteractions({
  allItemIds,
  buildRunsHref,
  changeViewMode,
  handleClearSelection,
  handleExitSelectMode,
  hideControls,
  isSelectMode,
  isShowingPlaceholderRows,
  navigateToHref,
  onReportLimitClose,
  onReportLimitOpen,
  openRun,
  openSimulation,
  resetScrollPosition,
  selectedCount,
  selectedKeys,
  tableRuns,
  tableSimulations,
  viewMode,
}: {
  allItemIds: string[]
  buildRunsHref: (path: string) => string
  changeViewMode: (mode: RunsViewMode) => void
  handleClearSelection: () => void
  handleExitSelectMode: () => void
  hideControls: boolean
  isSelectMode: boolean
  isShowingPlaceholderRows: boolean
  navigateToHref: (href: string) => void
  onReportLimitClose: () => void
  onReportLimitOpen: () => void
  openRun: (runId: string) => void
  openSimulation: (runId: string, simulationOutputId: string) => void
  resetScrollPosition: () => void
  selectedCount: number
  selectedKeys: RunsTableSelection
  tableRuns: RunsTableResult[]
  tableSimulations: SimulationsTableResult[]
  viewMode: RunsViewMode
}) {
  const pendingReportPathRef = useRef<string | null>(null)

  const handleViewModeChange = useCallback(
    (mode: RunsViewMode) => {
      if (mode !== viewMode) {
        handleExitSelectMode()
      }
      resetScrollPosition()
      changeViewMode(mode)
    },
    [changeViewMode, handleExitSelectMode, resetScrollPosition, viewMode],
  )

  const handleSwitchToRunsView = useCallback(() => {
    handleViewModeChange(RUNS_VIEW_MODE.RUNS)
  }, [handleViewModeChange])

  const handleSwitchToSimulationsView = useCallback(() => {
    handleViewModeChange(RUNS_VIEW_MODE.SIMULATIONS)
  }, [handleViewModeChange])

  const handleToggleViewMode = useCallback(() => {
    handleViewModeChange(
      viewMode === RUNS_VIEW_MODE.RUNS
        ? RUNS_VIEW_MODE.SIMULATIONS
        : RUNS_VIEW_MODE.RUNS,
    )
  }, [handleViewModeChange, viewMode])

  useEscapeShortcut({
    allowInInputs: true,
    description: "Clear selection",
    enabled: selectedCount > 0,
    handler: handleClearSelection,
    priority: ESCAPE_SHORTCUT_PRIORITY.SELECTION_CLEAR,
  })

  const currentCollectionItems = useMemo(
    () =>
      (viewMode === RUNS_VIEW_MODE.RUNS
        ? tableRuns
        : tableSimulations) as Array<RunsTableResult | SimulationsTableResult>,
    [tableRuns, tableSimulations, viewMode],
  )
  const {
    activeItemId: activeRowId,
    handlePointerEnter,
    handlePointerLeave,
  } = useCollectionPageShortcuts({
    enabled:
      !isSelectMode &&
      !isShowingPlaceholderRows &&
      currentCollectionItems.length > 0,
    getItemId: (item) => ("id" in item ? item.id : item.simulation_output_id),
    itemLabel: viewMode === RUNS_VIEW_MODE.RUNS ? "run" : "simulation",
    items: currentCollectionItems,
    onOpenItem: (item) => {
      if ("id" in item) {
        openRun(item.id)
        return
      }

      openSimulation(item.run_id, item.simulation_output_id)
    },
  })

  // Scoped by org + workspace so a workspace switch can't resurrect another
  // workspace's cached prev/next navigation order.
  const runsStorageKey = useScopedStorageKey(PAGE_NAVIGATION_KEYS.runs)
  // Scoped by org + workspace so a workspace switch can't resurrect another
  // workspace's cached prev/next navigation order.
  const simulationsStorageKey = useScopedStorageKey(
    PAGE_NAVIGATION_KEYS.simulations,
  )

  usePersistedCollectionNavigation({
    itemIds:
      viewMode === RUNS_VIEW_MODE.RUNS ? tableRuns.map((run) => run.id) : [],
    storageKey: runsStorageKey,
  })
  usePersistedCollectionNavigationItems({
    items:
      viewMode === RUNS_VIEW_MODE.SIMULATIONS
        ? tableSimulations.map((simulation) => ({
            id: simulation.simulation_output_id,
            parentId: simulation.run_id,
          }))
        : [],
    storageKey: simulationsStorageKey,
  })

  const selectedRunIds = useMemo(
    () =>
      viewMode === RUNS_VIEW_MODE.RUNS
        ? getSelectedRunIds(selectedKeys, allItemIds)
        : [],
    [allItemIds, selectedKeys, viewMode],
  )
  const selectedKeySet = useMemo(
    () => getSelectedRunKeySet(selectedKeys, allItemIds),
    [allItemIds, selectedKeys],
  )
  const selectedSimulations = useMemo(
    () =>
      viewMode === RUNS_VIEW_MODE.SIMULATIONS
        ? tableSimulations.filter((simulation) =>
            selectedKeySet.has(simulation.simulation_output_id),
          )
        : [],
    [selectedKeySet, tableSimulations, viewMode],
  )

  const handleCompareRuns = useCallback((): void => {
    if (isShowingPlaceholderRows) {
      return
    }

    const selectedSimulationOutputIds = selectedSimulations.map(
      (simulation) => simulation.simulation_output_id,
    )
    const selectedReportRunIds = Array.from(
      new Set(
        viewMode === RUNS_VIEW_MODE.RUNS
          ? selectedRunIds
          : selectedSimulations.map((simulation) => simulation.run_id),
      ),
    )

    if (selectedReportRunIds.length === 0) {
      return
    }

    const simulationOutputIds = selectedSimulationOutputIds.slice(
      0,
      MULTI_RUN_LIMIT,
    )
    const runIds = selectedReportRunIds.slice(0, MULTI_RUN_LIMIT)
    const params = new URLSearchParams({ run_ids: runIds.join(",") })
    if (simulationOutputIds.length > 0) {
      params.set("simulation_output_ids", simulationOutputIds.join(","))
    }
    const path = buildRunsHref(`/reports/new?${params.toString()}`)
    if (
      selectedSimulationOutputIds.length > MULTI_RUN_LIMIT ||
      selectedReportRunIds.length > MULTI_RUN_LIMIT
    ) {
      pendingReportPathRef.current = path
      onReportLimitOpen()
      return
    }
    navigateToHref(path)
  }, [
    buildRunsHref,
    isShowingPlaceholderRows,
    navigateToHref,
    onReportLimitOpen,
    selectedRunIds,
    selectedSimulations,
    viewMode,
  ])

  const handleReportLimitConfirm = useCallback(() => {
    const path = pendingReportPathRef.current
    pendingReportPathRef.current = null
    onReportLimitClose()
    if (path) {
      navigateToHref(path)
    }
  }, [navigateToHref, onReportLimitClose])

  const pageShortcuts = useMemo(
    (): PageShortcut[] => [
      {
        action: handleSwitchToRunsView,
        enabled:
          !hideControls && !isSelectMode && viewMode !== RUNS_VIEW_MODE.RUNS,
        key: "h",
        label: "Switch to Runs view",
      },
      {
        action: handleSwitchToRunsView,
        enabled:
          !hideControls && !isSelectMode && viewMode !== RUNS_VIEW_MODE.RUNS,
        key: "a",
        label: "Switch to Runs view",
        state: {
          hiddenInPalette: true,
        },
      },
      {
        action: handleSwitchToRunsView,
        enabled:
          !hideControls && !isSelectMode && viewMode !== RUNS_VIEW_MODE.RUNS,
        key: "ArrowLeft",
        label: "Switch to Runs view",
        state: {
          hiddenInPalette: true,
        },
      },
      {
        action: handleSwitchToSimulationsView,
        enabled:
          !hideControls &&
          !isSelectMode &&
          viewMode !== RUNS_VIEW_MODE.SIMULATIONS,
        key: "l",
        label: "Switch to Simulated view",
      },
      {
        action: handleSwitchToSimulationsView,
        enabled:
          !hideControls &&
          !isSelectMode &&
          viewMode !== RUNS_VIEW_MODE.SIMULATIONS,
        key: "d",
        label: "Switch to Simulated view",
        state: {
          hiddenInPalette: true,
        },
      },
      {
        action: handleSwitchToSimulationsView,
        enabled:
          !hideControls &&
          !isSelectMode &&
          viewMode !== RUNS_VIEW_MODE.SIMULATIONS,
        key: "ArrowRight",
        label: "Switch to Simulated view",
        state: {
          hiddenInPalette: true,
        },
      },
      {
        action: handleToggleViewMode,
        enabled: !hideControls && !isSelectMode,
        key: "v",
        label:
          viewMode === RUNS_VIEW_MODE.RUNS
            ? "Switch to Simulated view"
            : "Switch to Runs view",
      },
    ],
    [
      handleSwitchToRunsView,
      handleSwitchToSimulationsView,
      handleToggleViewMode,
      hideControls,
      isSelectMode,
      viewMode,
    ],
  )

  usePageShortcuts(pageShortcuts, !hideControls)

  const selectionCommands = useMemo(
    () => [
      {
        action: handleCompareRuns,
        available: selectedCount > 0 && !isShowingPlaceholderRows,
        description: `Generate a report from the selected ${
          viewMode === RUNS_VIEW_MODE.RUNS ? "runs" : "simulations"
        }.`,
        id: "runs-selection-create-report",
        kind: "create" as const,
        label: "Generate report from selection",
        scope: "selection" as const,
      },
      {
        action: handleClearSelection,
        available: selectedCount > 0,
        description: `Clear the current selected ${
          viewMode === RUNS_VIEW_MODE.RUNS ? "runs" : "simulations"
        }.`,
        id: "runs-selection-clear",
        kind: "back" as const,
        label:
          viewMode === RUNS_VIEW_MODE.RUNS
            ? "Clear selected runs"
            : "Clear selected simulations",
        scope: "selection" as const,
      },
    ],
    [
      handleClearSelection,
      handleCompareRuns,
      isShowingPlaceholderRows,
      selectedCount,
      viewMode,
    ],
  )

  usePageCommands(selectionCommands, !hideControls)

  return {
    activeRowId,
    handleCompareRuns,
    handlePointerEnter,
    handlePointerLeave,
    handleReportLimitConfirm,
    handleViewModeChange,
    selectedRunIds,
    selectedKeySet,
  }
}
